import React, { useEffect, useRef, useState } from "react";
import { Modal, Input, Button, Space, message, Row, Col, Typography, Tooltip } from "antd";
import { postReq } from "../api";
import Quill from "quill";
import "quill/dist/quill.snow.css";
import dayjs from "dayjs";

const { Text, Title } = Typography;

export default function CreateTemplateModal({ open, onClose, template, onSuccess }) {
  const isEdit = !!template;
  const editorRef = useRef(null);
  const quillInstance = useRef(null);

  // States for the inputs
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyContent, setBodyContent] = useState("");
  const [submitted, setSubmitted] = useState(false);

  // Initialize/Reset form on open/template change
  useEffect(() => {
    if (open) {
      setSubmitted(false);
      if (template) {
        setName(template.name);
        setSubject(template.subject);
        setBodyContent(template.body);
      } else {
        setName(`New Template - ${dayjs().format("DD MMM YYYY")}`);
        setSubject("");
        setBodyContent("");
      }

      // Initialize Quill after the modal renders the DOM
      const timer = setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.innerHTML = "";

          quillInstance.current = new Quill(editorRef.current, {
            theme: "snow",
            placeholder: "Compose your email body here...",
            modules: {
              toolbar: [
                ['bold', 'italic', 'underline'],
                [{ 'color': [] }],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                ['link', 'clean']
              ]
            }
          });

          // Set initial content
          if (template) {
            quillInstance.current.root.innerHTML = template.body;
          }

          // Handle text changes
          quillInstance.current.on('text-change', () => {
            setBodyContent(quillInstance.current.root.innerHTML);
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    } else {
      quillInstance.current = null;
    }
  }, [open, template]);

  // Insert variable helper
  const handleInsertPlaceholder = (placeholder, isSubject = false) => {
    if (isSubject) {
      setSubject((prev) => prev + placeholder);
    } else if (quillInstance.current) {
      const range = quillInstance.current.getSelection();
      if (range) {
        quillInstance.current.insertText(range.index, placeholder);
        quillInstance.current.setSelection(range.index + placeholder.length);
      } else {
        const length = quillInstance.current.getLength();
        quillInstance.current.insertText(length - 1, placeholder);
      }
    }
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    // Validation
    if (!name.trim() || !subject.trim() || !bodyContent.trim() || bodyContent === "<p><br></p>") {
      message.error("All fields (Title, Subject, and Body) are required");
      return;
    }

    const payload = {
      name,
      subject,
      body: bodyContent,
    };

    try {
      let res;
      if (isEdit) {
        res = await postReq(`/common/templates/${template._id}`, payload);
      } else {
        res = await postReq("/common/templates", payload);
      }

      if (res.status === 1) {
        message.success(res.message || "Template saved successfully");
        onClose();
        onSuccess();
      } else {
        message.error(res.message || "Failed to save template");
      }
    } catch (error) {
      console.error(error);
      message.error("An error occurred while saving the template");
    }
  };

  // Helper to check if body is empty (Quill empty state is usually <p><br></p>)
  const isBodyEmpty = !bodyContent || bodyContent.trim() === "<p><br></p>";

  return (
    <Modal
      open={open}
      title={null}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={1100}
      bodyStyle={{ padding: 0 }}
    >
      <Row style={{ minHeight: 550 }}>
        {/* =================================================================== */}
        {/* LEFT COLUMN: EDITOR FORM */}
        {/* =================================================================== */}
        <Col span={14} style={{ padding: 24, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
          <div>
            {/* Title Row */}
            <div style={{ display: "flex", alignItems: "center", border: "1px solid #d9d9d9", borderRadius: 4, padding: "6px 12px", marginBottom: 12 }}>
              <span style={{ color: "#8c8c8c", fontWeight: 500, width: 60 }}>Title</span>
              <div style={{ width: 1, height: 20, background: "#d9d9d9", margin: "0 12px" }} />
              <Input
                bordered={false}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Template Title"
                style={{ padding: 0, fontSize: 14 }}
              />
            </div>

            {/* Subject Row */}
            <div style={{ display: "flex", alignItems: "center", border: "1px solid #d9d9d9", borderRadius: 4, padding: "6px 12px", marginBottom: 16 }}>
              <span style={{ color: "#8c8c8c", fontWeight: 500, width: 60 }}>Subject</span>
              <div style={{ width: 1, height: 20, background: "#d9d9d9", margin: "0 12px" }} />
              <Input
                bordered={false}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Please enter subject..."
                style={{ padding: 0, fontSize: 14 }}
              />
              {/* Variable shortcut icons on the right */}
              <Space size="middle" style={{ marginLeft: "auto", color: "#8c8c8c", cursor: "pointer", fontSize: 14, fontWeight: "bold" }}>
                <Tooltip title="Insert First Name">
                  <span onClick={() => handleInsertPlaceholder("{{firstName}}", true)}>{"{}"}</span>
                </Tooltip>
                <Tooltip title="Insert Company Name">
                  <span onClick={() => handleInsertPlaceholder("{{companyName}}", true)}>{"{I}"}</span>
                </Tooltip>
              </Space>
            </div>

            {/* Quill Editor Container */}
            <div style={{
              border: "1px solid #d9d9d9",
              borderRadius: 4,
              overflow: "hidden",
              borderColor: submitted && isBodyEmpty ? "#ff4d4f" : "#d9d9d9"
            }}>
              {/* Insert buttons in editor toolbar area (we can just float them next to toolbar or below it) */}
              <div style={{ display: "flex", justifyContent: "space-between", background: "#fafafa", borderBottom: "1px solid #d9d9d9", padding: "4px 12px", alignItems: "center" }}>
                <span style={{ fontSize: 12, color: "#8c8c8c" }}>Rich Text Body</span>
                <Space size="middle" style={{ color: "#8c8c8c", cursor: "pointer", fontSize: 14, fontWeight: "bold" }}>
                  <Tooltip title="Insert First Name">
                    <span onClick={() => handleInsertPlaceholder("{{firstName}}")}>{"{}"}</span>
                  </Tooltip>
                  <Tooltip title="Insert Company Name">
                    <span onClick={() => handleInsertPlaceholder("{{companyName}}")}>{"{I}"}</span>
                  </Tooltip>
                </Space>
              </div>
              <div ref={editorRef} style={{ minHeight: 300, border: "none", fontSize: 14 }} />
            </div>

            {/* Validation warning */}
            {submitted && isBodyEmpty && (
              <div style={{ color: "#ff4d4f", marginTop: 6, fontSize: 13 }}>
                ⚠️ Content should not be empty
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div style={{ textAlign: "right", marginTop: 24 }}>
            <Space>
              <Button onClick={onClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleSubmit}
                style={{ background: "#13487D", borderColor: "#13487D" }}
              >
                Save
              </Button>
            </Space>
          </div>
        </Col>

        {/* =================================================================== */}
        {/* RIGHT COLUMN: EMAIL PREVIEW */}
        {/* =================================================================== */}
        <Col span={10} style={{ padding: 24, background: "#f8f9fa", borderLeft: "1px solid #e8e8e8", display: "flex", flexDirection: "column" }}>
          {/* Preview Tab Header */}
          <div style={{ borderBottom: "2px solid #13487D", display: "inline-block", alignSelf: "flex-start", paddingBottom: 8, marginBottom: 20 }}>
            <Space style={{ color: "#13487D", fontWeight: 600 }}>
              <span role="img" aria-label="mail">✉️</span>
              <span>Email Preview</span>
            </Space>
          </div>

          {/* Live Preview Display */}
          {!subject.trim() && isBodyEmpty ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1, color: "#bfbfbf", textAlign: "center" }}>
              <span style={{ fontSize: 52, marginBottom: 16 }}>✉️</span>
              <Text type="secondary" style={{ color: "#a6a6a6" }}>Write something to generate the Email preview</Text>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
              {/* Subject Preview */}
              <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid #e8e8e8" }}>
                <Text strong style={{ color: "#555" }}>Subject: </Text>
                <Text>{subject || <span style={{ color: "#bfbfbf", fontStyle: "italic" }}>No subject entered</span>}</Text>
              </div>

              {/* Body HTML Preview */}
              <div style={{
                flex: 1,
                padding: 16,
                background: "#ffffff",
                border: "1px solid #e8e8e8",
                borderRadius: 4,
                maxHeight: 420,
                overflowY: "auto"
              }}>
                {isBodyEmpty ? (
                  <span style={{ color: "#bfbfbf", fontStyle: "italic" }}>No body content entered</span>
                ) : (
                  <div className="ql-editor" style={{ padding: 0 }} dangerouslySetInnerHTML={{ __html: bodyContent }} />
                )}
              </div>
            </div>
          )}
        </Col>
      </Row>
    </Modal>
  );
}

// Utility import for date rendering

